import { Button, Card, Flex, Input, Space, Switch, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import type {
  InventorizationOutput,
  InventorizationTechnicalDetails,
} from '@/features/inventorization/domain/inventorization-technical';
import type { InventorizeReviewField } from '@/features/inventorization/domain/review/inventorize-review-fields';
import { messages } from '@/features/inventorization/messages';
import { ReviewStatusTag } from '@/routers/inventorization/components/ReviewStatusTag';

interface BuiltInOutputFieldWizardProps {
  field: InventorizeReviewField;
  onBack: () => void;
  onSaveTechnical: (patch: Partial<InventorizationTechnicalDetails>) => Promise<unknown> | unknown;
  technical: InventorizationTechnicalDetails;
}

export function BuiltInOutputFieldWizard({ field, onBack, onSaveTechnical, technical }: BuiltInOutputFieldWizardProps) {
  const intl = useIntl();
  const outputIndex = readOutputIndex(field.id);
  const output = outputIndex === null ? undefined : technical.outputs[outputIndex];
  const [draft, setDraft] = useState<InventorizationOutput | undefined>(output);

  useEffect(() => {
    setDraft(output);
  }, [output]);

  if (outputIndex === null || !draft) {
    return (
      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Typography.Text type="secondary">{field.section}</Typography.Text>
            <Typography.Title level={3} style={{ marginTop: 0 }}>
              {intl.formatMessage(messages.fieldWizardOutputUnavailableTitle)}
            </Typography.Title>
            <Typography.Paragraph type="secondary">
              {intl.formatMessage(messages.fieldWizardOutputUnavailableDescription)}
            </Typography.Paragraph>
          </div>
          <Button onClick={onBack}>{intl.formatMessage(messages.reviewActionsBackToReview)}</Button>
        </Space>
      </Card>
    );
  }

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Flex align="flex-start" gap="middle" justify="space-between" wrap>
          <div>
            <Typography.Text type="secondary">{field.section}</Typography.Text>
            <Typography.Title level={3} style={{ marginTop: 0 }}>
              {field.label}
            </Typography.Title>
            <Typography.Paragraph type="secondary">
              {intl.formatMessage(messages.fieldWizardOutputDescription)}
            </Typography.Paragraph>
          </div>
          <ReviewStatusTag status={field.status} />
        </Flex>

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Typography.Text type="secondary">
              {intl.formatMessage(messages.fieldWizardOutputTechnology)}
            </Typography.Text>
            <Typography.Title level={5} style={{ marginBottom: 0, marginTop: 4 }}>
              {draft.technology}
            </Typography.Title>
          </div>

          <Flex align="center" gap="middle" justify="space-between" wrap>
            <Space direction="vertical" size={2}>
              <Typography.Text strong>{intl.formatMessage(messages.fieldWizardOutputOccupied)}</Typography.Text>
              <Typography.Text type="secondary">
                {intl.formatMessage(messages.fieldWizardOutputOccupiedDescription)}
              </Typography.Text>
            </Space>
            <Switch
              checked={draft.isOccupied}
              checkedChildren={intl.formatMessage(messages.fieldWizardOutputOccupied)}
              onChange={checked =>
                setDraft(current =>
                  current
                    ? {
                        ...current,
                        isOccupied: checked,
                        remoteReadingOwner: checked ? current.remoteReadingOwner : '',
                      }
                    : current,
                )
              }
              unCheckedChildren={intl.formatMessage(messages.fieldWizardOutputFree)}
            />
          </Flex>

          {draft.isOccupied ? (
            <div>
              <Typography.Text>{intl.formatMessage(messages.fieldWizardOutputRemoteReadingOwner)}</Typography.Text>
              <Input
                onChange={event =>
                  setDraft(current =>
                    current
                      ? {
                          ...current,
                          remoteReadingOwner: event.target.value,
                        }
                      : current,
                  )
                }
                placeholder={intl.formatMessage(messages.fieldWizardOutputRemoteReadingOwnerPlaceholder)}
                value={draft.remoteReadingOwner}
              />
            </div>
          ) : null}
        </Space>

        <Flex gap="small" justify="space-between" wrap>
          <Button onClick={onBack}>{intl.formatMessage(messages.reviewActionsBackToReview)}</Button>
          <Button
            onClick={() => {
              const nextOutputs = technical.outputs.map((candidate, index) =>
                index === outputIndex
                  ? {
                      ...candidate,
                      isOccupied: draft.isOccupied,
                      remoteReadingOwner: draft.isOccupied ? draft.remoteReadingOwner : '',
                    }
                  : candidate,
              );

              onSaveTechnical({ outputs: nextOutputs });
              onBack();
            }}
            type="primary"
          >
            {intl.formatMessage(messages.reviewActionsSaveField)}
          </Button>
        </Flex>
      </Space>
    </Card>
  );
}

function readOutputIndex(fieldId: string) {
  const match = /^technical\.outputs\.(\d+)$/.exec(fieldId);

  return match ? Number(match[1]) : null;
}
